import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import Landing from "@/pages/Landing";
import ProjectView from "@/pages/ProjectView";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import Terms from "@/pages/Terms";
import AccountSettings from "@/pages/AccountSettings";
import AuthCallback from "@/pages/AuthCallback";
import AcceptTransfer from "@/pages/AcceptTransfer";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import BillingSuccess from "@/pages/BillingSuccess";
import StandUp from "@/pages/StandUp";
import StandUpSetup from "@/pages/StandUpSetup";
import StandUpManage from "@/pages/StandUpManage";
import StandUpLaunch from "@/pages/StandUpLaunch";
import StandUpSummary from "@/pages/StandUpSummary";
import { StandUpRouter, StandUpHost, StandUpParticipant, StandUpJoin } from "@/pages/StandUpSession";

function AppRouter() {
  const location = useLocation();
  // Reset scroll on every pathname change. Without this, react-router preserves
  // the previous page's scroll position, so footer links to /about from the
  // bottom of /pricing land you halfway down /about. Hash links (e.g. #faq) are
  // intentionally NOT touched so deep-anchor links keep working.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
    }
  }, [location.pathname, location.hash]);
  // Synchronous check — process session_id BEFORE anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/settings" element={<AccountSettings />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/transfer/accept" element={<AcceptTransfer />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/standup" element={<StandUp />} />
      <Route path="/standup/new" element={<StandUpSetup />} />
      <Route path="/standup/manage" element={<StandUpManage />} />
      <Route path="/standup/session/:sessionId/launch" element={<StandUpLaunch />} />
      <Route path="/standup/session/:sessionId/route" element={<StandUpRouter />} />
      <Route path="/standup/session/:sessionId/host" element={<StandUpHost />} />
      <Route path="/standup/session/:sessionId/participant" element={<StandUpParticipant />} />
      <Route path="/standup/session/:sessionId/summary" element={<StandUpSummary />} />
      <Route path="/j/:token" element={<StandUpJoin />} />
      <Route path="/project/:projectId" element={<ProjectView />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.title = "ThatGantt";
  }, []);

  return (
    <div className="App min-h-screen bg-slate-50 text-slate-950 dark:bg-[#0d0a10] dark:text-mint">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
